<template>
  <div v-if="estado.asistente" class="ayuda-mg">
    <button
      v-show="!capturando"
      type="button"
      class="ayuda-mg__boton"
      :class="{ 'ayuda-mg__boton--abierto': abierto }"
      :title="abierto ? 'Cerrar la ayuda' : 'Ayuda'"
      @click="abierto = !abierto"
    >
      <i :class="abierto ? 'iconoir-cancel' : 'iconoir-chat-bubble-question'"></i>
    </button>

    <section v-if="abierto && !capturando" class="ayuda-mg__panel" aria-label="Ayuda de Sync">
      <header class="ayuda-mg__cabecera">
        <div>
          <div class="ayuda-mg__ceja">Ayuda</div>
          <div class="ayuda-mg__titulo">Sync</div>
        </div>
        <button type="button" class="ayuda-mg__cerrar" title="Cerrar" @click="abierto = false">
          <i class="iconoir-cancel"></i>
        </button>
      </header>

      <div ref="mensajes" class="ayuda-mg__mensajes">
        <div class="ayuda-mg__mensaje ayuda-mg__mensaje--asistente">
          Hola, {{ nombre }}. ¿En qué te ayudo? Pregúntame cómo hacer algo en Sync.
        </div>
        <div v-if="!mensajes.length" class="ayuda-mg__sugerencias">
          <button
            v-for="s in sugerencias"
            :key="s"
            type="button"
            class="ayuda-mg__sugerencia"
            @click="enviar(s)"
          >
            {{ s }}
          </button>
        </div>
        <template v-for="(m, i) in mensajes" :key="i">
          <div
            class="ayuda-mg__mensaje"
            :class="'ayuda-mg__mensaje--' + m.rol"
            v-html="m.rol === 'asistente' ? formato(m.texto) : escapar(m.texto)"
          ></div>
          <button
            v-if="m.ofrecerTicket && estado.tickets"
            type="button"
            class="ayuda-mg__ticket"
            @click="levantarTicket"
          >
            <i class="iconoir-warning-circle"></i> Levantar ticket de soporte
          </button>
        </template>
        <div v-if="pensando" class="ayuda-mg__mensaje ayuda-mg__mensaje--asistente ayuda-mg__pensando">
          <span></span><span></span><span></span>
        </div>
        <div v-if="hecho" class="ayuda-mg__hecho">
          <i class="iconoir-check-circle"></i> {{ hecho }}
        </div>
      </div>

      <form class="ayuda-mg__pie" @submit.prevent="enviar()">
        <textarea
          v-model="borrador"
          class="ayuda-mg__campo"
          rows="2"
          maxlength="2000"
          placeholder="Escribe tu pregunta…"
          @keydown.enter.exact.prevent="enviar()"
        ></textarea>
        <button type="submit" class="ayuda-mg__enviar" :disabled="!borrador.trim() || pensando" title="Enviar">
          <i class="iconoir-send"></i>
        </button>
      </form>
      <button v-if="estado.tickets" type="button" class="ayuda-mg__enlace" @click="levantarTicket">
        ¿No se resolvió? Levantar un ticket de soporte
      </button>
    </section>

    <TicketSoporteMG
      v-if="captura !== null"
      :captura="captura"
      :conversacion="mensajes"
      @cancelar="captura = null"
      @enviado="ticketEnviado"
    />
  </div>
</template>

<script>
import AyudaService from '@extras_mg/services/ayuda'
import TicketSoporteMG from '@extras_mg/components/TicketSoporteMG'

/**
 * El asistente de ayuda: botón flotante en todas las páginas con sesión.
 *
 * La conversación vive aquí (en memoria) y viaja completa en cada pregunta:
 * el servidor no guarda nada. Si el servidor no tiene llave de Anthropic el
 * botón ni aparece.
 *
 * El ticket toma una foto de la página tal como está (sin el panel encima),
 * para que la persona señale el error sobre ella. Si la foto falla, el ticket
 * sigue: va sin captura.
 */
export default {
  name: 'AyudaMG',
  components: { TicketSoporteMG },
  data() {
    return {
      estado: { asistente: false, tickets: false },
      abierto: false,
      mensajes: [],
      borrador: '',
      pensando: false,
      capturando: false,
      captura: null,
      hecho: '',
      sugerencias: [
        '¿Cómo agrego un asunto?',
        '¿Cómo subo un documento?',
        '¿Cómo paso un asunto a Completadas?',
        '¿Cómo invito a alguien de mi equipo?',
      ],
    }
  },
  computed: {
    nombre() {
      return (this.$store.getters['auth/getName'] || '').split(' ')[0] || 'hola'
    },
  },
  async mounted() {
    try {
      const { data } = await AyudaService(this.$client).estado()
      this.estado = data
    } catch (e) {}
  },
  methods: {
    escapar(texto) {
      return String(texto)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },
    /** Markdown mínimo y seguro: se escapa todo primero, luego negritas y listas. */
    formato(texto) {
      const lineas = this.escapar(texto).split('\n')
      let html = ''
      let lista = null
      const cerrar = () => {
        if (lista) html += `</${lista}>`
        lista = null
      }
      for (const linea of lineas) {
        const numerada = linea.match(/^\s*\d+[.)]\s+(.*)$/)
        const vineta = linea.match(/^\s*[-•*]\s+(.*)$/)
        const tipo = numerada ? 'ol' : vineta ? 'ul' : null
        if (tipo) {
          if (lista !== tipo) {
            cerrar()
            html += `<${tipo}>`
            lista = tipo
          }
          html += `<li>${(numerada || vineta)[1]}</li>`
        } else {
          cerrar()
          if (linea.trim()) html += `<p>${linea}</p>`
        }
      }
      cerrar()
      return html.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
    },
    bajar() {
      this.$nextTick(() => {
        const caja = this.$refs.mensajes
        if (caja) caja.scrollTop = caja.scrollHeight
      })
    },
    async enviar(texto) {
      const pregunta = (texto ?? this.borrador).trim()
      if (!pregunta || this.pensando) return
      this.hecho = ''
      this.borrador = ''
      this.mensajes.push({ rol: 'usuario', texto: pregunta })
      this.pensando = true
      this.bajar()
      try {
        const { data } = await AyudaService(this.$client).preguntar(
          // Los avisos de error del propio panel no son parte de la conversación.
          this.mensajes.filter((m) => !m.sinHistorial).map(({ rol, texto }) => ({ rol, texto })),
          window.location.pathname
        )
        this.estado.tickets = data.tickets
        this.mensajes.push({ rol: 'asistente', texto: data.texto, ofrecerTicket: data.ofrecer_ticket })
      } catch (e) {
        const detalle = e.response?.data?.detail
        this.mensajes.push({
          rol: 'asistente',
          texto: detalle || 'No pude responder en este momento. Intenta de nuevo en un rato.',
          ofrecerTicket: true,
          sinHistorial: true,
        })
      } finally {
        this.pensando = false
        this.bajar()
      }
    },
    async levantarTicket() {
      // Se esconde el panel y el botón para que no salgan en la foto.
      this.capturando = true
      await this.$nextTick()
      await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 60)))
      let foto = ''
      try {
        const { toCanvas } = await import('html-to-image')
        const lienzo = await toCanvas(document.body, {
          pixelRatio: Math.min(window.devicePixelRatio || 1, 1.5),
          width: window.innerWidth,
          height: window.innerHeight,
          cacheBust: true,
          filter: (nodo) => !nodo.classList?.contains('ayuda-mg'),
        })
        foto = lienzo.toDataURL('image/png')
      } catch (e) {
        foto = ''
      }
      this.capturando = false
      this.captura = foto
    },
    ticketEnviado(numero) {
      this.captura = null
      this.abierto = true
      this.hecho = `Listo: tu ticket #${numero} quedó registrado y Victor ya lo tiene. Te va a contactar.`
      this.bajar()
    },
  },
}
</script>

<style lang="scss">
.ayuda-mg__boton {
  position: fixed;
  right: 22px;
  bottom: 64px;
  z-index: 40;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: 0;
  cursor: pointer;
  color: #ffffff;
  background: #0e0e0f;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
  transition: transform 0.12s ease;
}

.ayuda-mg__boton:hover {
  transform: scale(1.06);
}

.ayuda-mg__panel {
  position: fixed;
  right: 22px;
  bottom: 124px;
  z-index: 40;
  width: 380px;
  max-width: calc(100vw - 32px);
  height: min(560px, calc(100vh - 150px));
  display: flex;
  flex-direction: column;
  background: var(--mg-fondo, #ffffff);
  color: var(--mg-texto, #323338);
  border: 1px solid var(--mg-borde, #d0d4e4);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.2);
}

.ayuda-mg__cabecera {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  color: #ffffff;
  background: #0e0e0f;
}

.ayuda-mg__ceja {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #9a9a9a;
}

.ayuda-mg__titulo {
  font-size: 16px;
  font-weight: 300;
  letter-spacing: 0.02em;
}

.ayuda-mg__cerrar {
  border: 0;
  background: transparent;
  color: #ffffff;
  font-size: 20px;
  cursor: pointer;
}

.ayuda-mg__mensajes {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ayuda-mg__mensaje {
  max-width: 88%;
  padding: 9px 12px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.45;
  overflow-wrap: anywhere;

  p {
    margin: 0 0 6px;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ol,
  ul {
    margin: 4px 0 6px;
    padding-left: 18px;
  }

  ol {
    list-style: decimal;
  }

  ul {
    list-style: disc;
  }
}

.ayuda-mg__mensaje--asistente {
  align-self: flex-start;
  background: color-mix(in srgb, currentColor 7%, transparent);
  border-bottom-left-radius: 4px;
}

.ayuda-mg__mensaje--usuario {
  align-self: flex-end;
  color: #ffffff;
  background: #0e0e0f;
  border-bottom-right-radius: 4px;
  white-space: pre-wrap;
}

.ayuda-mg__sugerencias {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ayuda-mg__sugerencia {
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid var(--mg-borde, #d0d4e4);
  background: transparent;
  color: inherit;
  font-size: 12px;
  cursor: pointer;
}

.ayuda-mg__sugerencia:hover {
  border-color: #0e0e0f;
}

.ayuda-mg__ticket {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 8px;
  border: 1px solid #0e0e0f;
  background: transparent;
  color: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.ayuda-mg__hecho {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  padding: 9px 12px;
  border-radius: 10px;
  font-size: 13px;
  color: #0b6b3a;
  background: color-mix(in srgb, #00c875 14%, transparent);
}

.ayuda-mg__pensando {
  display: flex;
  gap: 4px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.4;
    animation: ayuda-mg-punto 1s infinite ease-in-out;
  }

  span:nth-child(2) {
    animation-delay: 0.15s;
  }

  span:nth-child(3) {
    animation-delay: 0.3s;
  }
}

@keyframes ayuda-mg-punto {
  50% {
    opacity: 1;
  }
}

.ayuda-mg__pie {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  padding: 10px 12px 6px;
  border-top: 1px solid var(--mg-borde, #d0d4e4);
}

.ayuda-mg__campo {
  flex: 1;
  resize: none;
  border: 1px solid var(--mg-borde, #d0d4e4);
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
  color: inherit;
  background: transparent;
}

.ayuda-mg__enviar {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 0;
  color: #ffffff;
  background: #0e0e0f;
  font-size: 18px;
  cursor: pointer;
}

.ayuda-mg__enviar:disabled {
  opacity: 0.35;
  cursor: default;
}

.ayuda-mg__enlace {
  border: 0;
  background: transparent;
  color: inherit;
  opacity: 0.7;
  font-size: 11px;
  padding: 2px 12px 10px;
  text-align: left;
  text-decoration: underline;
  cursor: pointer;
}
</style>
